<template>
  <Teleport to="[data-header-action]" v-if="isMounted">
    <div class="[ flex items-center gap-6 ]">
      <DemoUploadButton v-user-can="`Demo.Write`" />
    </div>
  </Teleport>

  <section class="[ flex gap-3 h-full ]">
    <div class="[ flex-1 px-5 py-6 pb-0 flex flex-col gap-4 h-full max-w-7xl m-auto ]">
      <div class="[ flex-1 flex gap-12 items-start h-full ]">
        <div class="[ sticky top-6 w-72 grid gap-3 ]">
          <TeamNavigationButtonList :current-teams="filters.teams" @select-team="setTeamFilter" />
        </div>

        <div class="[ flex-1 h-full flex flex-col gap-4 ]">
          <h1 class="[ font-bold text-xl ]">{{ filteredTeamName }}</h1>
          <div class="[ flex gap-3 justify-between ]">
            <DateRangeRadio :intervals="dateRangeIntervals" :range-options="dateRanges" v-model="dateRangeIndex"
              @custom-date-selected="setCustomDateRangeValue" />
          </div>

          <Card>
            <template #content>
              <JBChart :height="100" type="line" :data="chartData" :options="chartOptions" :loading="loadingTimeseries"
                :class="{ 'opacity-50': loadingTimeseries }" />
            </template>
          </Card>

          <!--          <HighlightCard>-->
          <!--            <template #title>-->
          <!--              <div class="[ flex font-normal items-center justify-between">-->
          <!--                <div class="[ flex gap-3 items-center ]">-->
          <!--                  <div class="[ border-r pr-3 ]">-->
          <!--                    <JBIcon name="calendar" class="[ text-gray-dark ]" />-->
          <!--                  </div>-->
          <!--                  <ButtonComponent-->
          <!--                    :label="dateRangeStartLabel"-->
          <!--                    plain-->
          <!--                    severity="secondary"-->
          <!--                    size="small"-->
          <!--                    @click="(e) => calendarPopoverStart?.toggle(e)"-->
          <!--                  >-->
          <!--                    <template #icon>-->
          <!--                      <JBIcon name="chevron-down" />-->
          <!--                    </template>-->
          <!--                  </ButtonComponent>-->
          <!--                  <span class="[ text-sm text-gray-dark ]">to</span>-->
          <!--                  <ButtonComponent-->
          <!--                    :label="dateRangeEndLabel"-->
          <!--                    plain-->
          <!--                    severity="secondary"-->
          <!--                    size="small"-->
          <!--                    @click="(e) => calendarPopoverEnd?.toggle(e)"-->
          <!--                  >-->
          <!--                    <template #icon>-->
          <!--                      <JBIcon name="chevron-down" />-->
          <!--                    </template>-->
          <!--                  </ButtonComponent>-->
          <!--                </div>-->
          <!--              </div>-->
          <!--            </template>-->
          <!--            <template #content>-->
          <!--              <StatsSummary :stats="contactsStats" :loading="loadingStats" />-->
          <!--            </template>-->
          <!--          </HighlightCard>-->
          <ContactHistoryFilters :model-value="filters" :available-filters="['agent', 'rating']" />
          <div class="[ flex-1 min-h-80 overflow-auto ]">
            <ContactHistoryTable :filters="filters" :date-range="dateRange" />
          </div>
        </div>
      </div>
    </div>
  </section>
  <Popover ref="calendarPopoverStart">
    <DatePicker inline showButtonBar v-model="dateRange[0]" />
  </Popover>
  <Popover ref="calendarPopoverEnd">
    <DatePicker inline showButtonBar v-model="dateRange[1]" />
  </Popover>
</template>

<script setup lang="ts">
import ContactHistoryFilters from "@/components/contact/qa/ContactHistoryFilters.vue";
import ContactHistoryTable from "@/components/contact/qa/ContactHistoryTable.vue";
// import HighlightCard from "@/components/ui/HighlightCard.vue";
// import StatsSummary from "@/components/ui/StatsSummary.vue";
import TeamNavigationButtonList from "@/components/agents/team/TeamNavigationButtonList.vue";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import DemoUploadButton from "@/components/contact/qa/DemoUploadButton.vue";
import { ContactFilters, useTeamStore } from "@/store/admin/team.ts";
import DatePicker from "primevue/datepicker";
import Popover from "primevue/popover";
import { storeToRefs } from "pinia";
import { Team } from "@/services/api/sdk";
import {
  dateIntervals,
  dateRangeIntervals,
  dateRanges,
  getDateIntervalForCustomDateRange,
} from "@/utils/contacts/filters.ts";
import DateRangeRadio from "@/components/contact/qa/DateRangeRadio.vue";
import JBChart from "@/components/ui/JBChart.vue";
import { useContactsTimeseries } from "@/composables/contacts-timeseries.ts";
import { Card } from "primevue";
import { useContactTimeseriesChart } from "@/utils/ui/charts/contacts-timeseries.ts";
import { useApi } from "@/services/api";

definePage({
  name: "calls/call-history",
  meta: {
    icon: "history",
    title: "Call history",
    scope: "QA.Read",
  },
});

const route = useRoute();
const { allTeams } = storeToRefs(useTeamStore());

const calendarPopoverStart = ref();
const calendarPopoverEnd = ref();

const isMounted = ref<boolean>(false);
const dateRangeIndex = ref(0);
const filters = ref<ContactFilters>({
  users: [],
  teams: [],
  customerPhoneQuery: "",
  ratingRange: [0, 1],
});

const customDateRange = ref<[Date | null, Date | null]>([null, null]);

const hasSelectedCustomDateRange = computed<boolean>(() => {
  const [start, end] = customDateRange.value;
  return !!(start && end);
});

const dateRange = computed(() => {
  if (hasSelectedCustomDateRange.value) return customDateRange.value;
  return dateRanges[dateRangeIndex.value];
});

const dateInterval = computed(() => {
  if (hasSelectedCustomDateRange.value)
    return getDateIntervalForCustomDateRange(
      customDateRange.value! as [Date, Date],
    );
  return dateIntervals[dateRangeIndex.value];
});

const { loadingTimeseries, contactsTimeseries } = useContactsTimeseries(
  dateRange,
  dateInterval,
  filters,
);

const { chartOptions, chartData } = useContactTimeseriesChart({
  contactsTimeseries,
  dateRange,
  dateInterval,
});

function setTeamFilter(teamId: number) {
  if (teamId) {
    filters.value.teams = [teamId];
  } else {
    filters.value.teams = [];
  }
}

const currentlyFilteredTeam = computed<Team | undefined>(() => {
  if (!filters.value.teams.length) return undefined;

  return allTeams.value.find((team) => team.id === filters.value.teams[0]);
});

const filteredTeamName = computed<string | undefined>(() => {
  if (!filters.value.teams.length) {
    return "All teams";
  }

  return currentlyFilteredTeam.value?.name;
});

function setCustomDateRangeValue(
  v: Date | (Date | null)[] | Date[] | null | undefined,
) {
  customDateRange.value = v as [Date, Date];
}

</script>

<style lang="scss">
.slideInLeft-enter-active {
  animation: slideInLeft 0.35s;
}

.slideInLeft-leave-active {
  animation: slideInLeft 0.35s reverse;
}

@keyframes slideInLeft {
  0% {
    margin-right: -640px;
    position: relative;
  }

  100% {
    margin-right: 0;
    position: relative;
  }
}
</style>
